import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Hooks, Util } from "@xt/xtbase";
import { getChannels, getOrders } from "@/api/v4/payment";
import { formatStatusText } from "./index";
import { createCreditTranslator } from "./i18n";

const { useTranslation } = Hooks;
const { getUrl } = Util;

const PAGE_SIZE = 10;

export default function ThirdPartyBuyRecords({ loginStatus }) {
  const t = useTranslation();
  const tx = useMemo(() => createCreditTranslator(t), [t]);
  const loadingLabel = tx("common.loading", "Loading...");

  const [channelTabs, setChannelTabs] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState("");
  const [orders, setOrders] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const list = await getChannels({ isAutoPopError: false });
        if (cancelled) return;
        const tabs = Array.isArray(list) ? list : [];
        setChannelTabs(tabs);
        setActiveChannelId(String(tabs[0]?.channelId || ""));
      } catch (error) {
        console.error("load order channels failed", error);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadOrders = useCallback(
    async (nextPage = 1, append = false) => {
      if (!loginStatus || !activeChannelId) {
        setOrders([]);
        return;
      }
      setLoading(true);
      try {
        const res = await getOrders({
          params: {
            pageIndex: nextPage,
            pageSize: PAGE_SIZE,
            channelId: activeChannelId,
          },
          isAutoPopError: false,
        });
        const items = Array.isArray(res?.items) ? res.items : [];
        setHasNext(Boolean(res?.hasNext) || items.length >= PAGE_SIZE);
        setOrders((prev) => (append ? [...prev, ...items] : items));
        setPageIndex(nextPage);
      } catch (error) {
        console.error("load third-party orders failed", error);
        if (!append) setOrders([]);
      } finally {
        setLoading(false);
      }
    },
    [activeChannelId, loginStatus]
  );

  useEffect(() => {
    if (!activeChannelId) return;
    loadOrders(1, false);
  }, [activeChannelId, loadOrders]);

  const empty = useMemo(() => !loading && orders.length === 0, [loading, orders.length]);

  if (!loginStatus) {
    return (
      <div className="tpr-page">
        <div className="tpr-emptyBlock">
          <div className="tpr-title">{tx("credit.OrderHistory", "Order History")}</div>
          <div className="tpr-emptyText">
            {tx("credit.notLogin1", "You did not log in yet, please try after")}{" "}
            <a href={getUrl("/accounts/login")} className="tpr-link">
              {tx("credit.login", "Login")}
            </a>{" "}
            {tx("credit.notLoginSeparator", "/")}{" "}
            <a href={getUrl("/accounts/register")} className="tpr-link">
              {tx("credit.Signup", "Sign up")}
            </a>
          </div>
        </div>
        <style jsx>{`
          .tpr-page {
            min-height: 100vh;
            background: #181a20;
            color: #fff;
            padding: 16px;
          }
          .tpr-emptyBlock {
            background: #1a1c20;
            border-radius: 12px;
            padding: 18px 16px;
          }
          .tpr-title {
            font-size: 18px;
            font-weight: 700;
            margin-bottom: 16px;
          }
          .tpr-emptyText {
            color: rgba(255, 255, 255, 0.6);
            font-size: 14px;
            line-height: 22px;
          }
          .tpr-link {
            color: #b9f78a;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="tpr-page">
      <div className="tpr-header">{tx("credit.OrderHistory", "Order History")}</div>
      <div className="tpr-tabs">
        {channelTabs.map((item) => (
          <button
            key={item.channelId}
            type="button"
            className={String(item.channelId) === String(activeChannelId) ? "active" : ""}
            onClick={() => setActiveChannelId(String(item.channelId))}
          >
            {item.channel}
          </button>
        ))}
      </div>

      <div className="tpr-list">
        {loading && orders.length === 0 ? <div className="tpr-loading">{loadingLabel}</div> : null}
        {empty ? <div className="tpr-loading">{tx("credit.NoOrdersYet", "No orders yet")}</div> : null}
        {orders.map((item, index) => {
          const isBuy = String(item.orderType || "").toLowerCase().includes("buy");
          return (
            <div key={item.id || `${item.createdTime}-${index}`} className="tpr-card">
              <div className="tpr-cardHead">
                <span className={isBuy ? "buy" : "sell"}>{isBuy ? tx("credit.BuyCrypto", "Buy Crypto") : tx("credit.SellCrypto", "Sell Crypto")}</span>
                <span className="tpr-time">{dayjs(item.createdTime).format("YYYY-MM-DD HH:mm:ss")}</span>
              </div>
              <div className="tpr-row">
                <span>{tx("credit.Amount", "Amount")}</span>
                <span className={isBuy ? "buy" : "sell"}>
                  {isBuy ? `${item.cryptoAmount || "--"} ${item.crypto || ""}` : `${item.fiatAmount || "--"} ${item.fiat || ""}`}
                </span>
              </div>
              <div className="tpr-row">
                <span>{tx("credit.Currency(Feesincluded)", "Currency(Fees included)")}</span>
                <span>{isBuy ? `${item.fiatAmount || "--"} ${item.fiat || ""}` : `${item.cryptoAmount || "--"} ${item.crypto || ""}`}</span>
              </div>
              <div className="tpr-row">
                <span>{tx("credit.Status", "Status")}</span>
                <span>{formatStatusText(item.status, tx)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {hasNext && orders.length > 0 ? (
        <button type="button" className="tpr-more" disabled={loading} onClick={() => loadOrders(pageIndex + 1, true)}>
          {loading ? loadingLabel : tx("credit.LoadMore", "Load More")}
        </button>
      ) : null}

      <style jsx>{`
        .tpr-page {
          min-height: 100vh;
          background: #181a20;
          color: #fff;
          padding: 16px;
        }
        .tpr-header {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 16px;
        }
        .tpr-tabs {
          display: flex;
          gap: 16px;
          overflow-x: auto;
          margin-bottom: 16px;
          padding-bottom: 6px;
        }
        .tpr-tabs button {
          flex-shrink: 0;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.45);
          font-size: 14px;
          padding: 0 0 10px;
          position: relative;
        }
        .tpr-tabs button.active {
          color: #fff;
          font-weight: 600;
        }
        .tpr-tabs button.active::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 2px;
          background: #b9f78a;
          border-radius: 1px;
        }
        .tpr-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .tpr-card {
          background: #1a1c20;
          border: 1px solid rgba(255, 255, 255, 0.04);
          border-radius: 12px;
          padding: 14px;
        }
        .tpr-cardHead,
        .tpr-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .tpr-cardHead {
          margin-bottom: 14px;
        }
        .tpr-time {
          font-size: 11px;
          color: rgba(255, 255, 255, 0.45);
        }
        .tpr-row {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.65);
          margin-top: 10px;
        }
        .tpr-row span:last-child {
          color: #fff;
          text-align: right;
        }
        .buy {
          color: #45bf59 !important;
          font-weight: 600;
        }
        .sell {
          color: #d94f4f !important;
          font-weight: 600;
        }
        .tpr-loading {
          border-radius: 12px;
          background: #1a1c20;
          padding: 24px 16px;
          text-align: center;
          color: rgba(255, 255, 255, 0.45);
          font-size: 13px;
        }
        .tpr-more {
          width: 100%;
          margin-top: 16px;
          height: 42px;
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(255, 255, 255, 0.04);
          color: #fff;
          font-size: 14px;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
